import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../shared/api';
import { API_URL, getAuthHeaders } from './helpers';
import { useToast, useConfirm } from '../shared/AppDialogs';

// Tabs
import { BasicsTab } from './tabs/BasicsTab';
import { VoiceTab } from './tabs/VoiceTab';
import { KnowledgeTab } from './tabs/KnowledgeTab';
import { ToolsTab } from './tabs/ToolsTab';
import { AutomationTab } from './tabs/AutomationTab';
import { OutboundTab } from './tabs/OutboundTab';
import { ConnectionsTab } from './tabs/ConnectionsTab';

// Preview & Modals
import { LivePreview } from './preview/LivePreview';
import { ToolConfigModal } from './modals/ToolConfigModal';

export function AgentDetailPage({ agentId, onBack, onSave, onDirtyChange }) {
  const toast = useToast();
  const confirm = useConfirm();
  // Core state
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basics');
  const [editingTool, setEditingTool] = useState(null);
  const [showToolModal, setShowToolModal] = useState(false);
  const [formData, setFormData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savedFormSnapshot, setSavedFormSnapshot] = useState(null);

  // ElevenLabs state
  const [elVoices, setElVoices] = useState([]);
  const [elVoicesLoading, setElVoicesLoading] = useState(false);
  const [elMessage, setElMessage] = useState(null);
  const [playingVoice, setPlayingVoice] = useState(null);
  const [audioRefEditor] = useState({ current: null });
  const [syncStatus, setSyncStatus] = useState(null);
  const [checkingSync, setCheckingSync] = useState(false);

  // Preview state
  const [previewCollapsed, setPreviewCollapsed] = useState(true);
  const [previewMode, setPreviewMode] = useState('chat');

  // Outbound call state
  const [outboundNumber, setOutboundNumber] = useState('');
  const [outboundContext, setOutboundContext] = useState('');
  const [outboundLoading, setOutboundLoading] = useState(false);
  const [outboundResult, setOutboundResult] = useState(null);
  const [activeCallSid, setActiveCallSid] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [callStartTime, setCallStartTime] = useState(null);
  const [callElapsed, setCallElapsed] = useState(0);

  // Twilio test state
  const [twilioTestLoading, setTwilioTestLoading] = useState(false);
  const [twilioTestResult, setTwilioTestResult] = useState(null);

  // Twilio ElevenLabs connect
  const [elConnecting, setElConnecting] = useState(false);

  // Mobile tab dropdown
  const [mobileTabsOpen, setMobileTabsOpen] = useState(false);

  // Prompt optimizer
  const [optimizing, setOptimizing] = useState(false);

  // Knowledge Base state
  const [knowledgeSources, setKnowledgeSources] = useState([]);
  const [newUrl, setNewUrl] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [scrapeWithJs, setScrapeWithJs] = useState(true);
  const [includeSubpages, setIncludeSubpages] = useState(false);
  const [foundSubpages, setFoundSubpages] = useState([]);
  const [selectedSubpages, setSelectedSubpages] = useState([]);
  const [subpageLoading, setSubpageLoading] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // ========== EFFECTS ==========

  // Call status polling
  useEffect(() => {
    if (!activeCallSid) return;
    const pollStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/calls/${activeCallSid}/status`, { headers: getAuthHeaders() });
        if (!response.ok) return;
        const data = await response.json();
        const newStatus = data.status;
        if (newStatus === 'ringing' || newStatus === 'initiated') {
          setCallStatus('ringing');
        } else if (newStatus === 'in_progress' || newStatus === 'calling' || newStatus === 'ongoing') {
          setCallStatus(prev => {
            if (prev !== 'in_progress') { setCallStartTime(Date.now()); setCallElapsed(0); }
            return 'in_progress';
          });
        } else if (['completed', 'failed', 'busy', 'no-answer', 'canceled'].includes(newStatus)) {
          setCallStatus(newStatus);
          setCallStartTime(null);
          setCallElapsed(0);
          const durationSecs = data.duration || 0;
          const mins = Math.floor(durationSecs / 60);
          const secs = String(durationSecs % 60).padStart(2, '0');
          setOutboundResult(prev => ({ ...prev, finalStatus: newStatus, duration: durationSecs, message: newStatus === 'completed' ? `Anruf beendet${durationSecs > 0 ? ` (${mins}:${secs} Min)` : ''}` : newStatus === 'failed' ? 'Anruf fehlgeschlagen' : 'Anruf beendet' }));
          setActiveCallSid(null);
        }
      } catch (error) { /* poll error ignored */ }
    };
    pollStatus();
    const interval = setInterval(pollStatus, 3000);
    const timeout = setTimeout(() => setActiveCallSid(null), 600000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [activeCallSid]);

  // Call timer
  useEffect(() => {
    if (!activeCallSid || callStatus !== 'in_progress' || !callStartTime) return;
    const timerInterval = setInterval(() => setCallElapsed(Math.floor((Date.now() - callStartTime) / 1000)), 1000);
    return () => clearInterval(timerInterval);
  }, [activeCallSid, callStatus, callStartTime]);

  // Load ElevenLabs voices
  // Load voices on mount (intentionally run once)
  useEffect(() => { loadElVoices(); }, []); // eslint-disable-line

  // Load agent data
  useEffect(() => {
    const loadAgent = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const result = await api.get(`/agents/${agentId}`);
        if (result.success && result.data) {
          setAgent(result.data);
          setFormData({
            name: result.data.name || '', description: result.data.description || '',
            system_prompt: result.data.system_prompt || '', knowledge_base: result.data.knowledge_base || '',
            knowledge_sources: result.data.knowledge_sources || [], voice: result.data.voice || '',
            language: result.data.language || 'de', phone_number: result.data.phone_number || '',
            is_active: result.data.is_active ?? true, tools: result.data.tools || [],
            webhook_config: result.data.webhook_config || { url: '', enabled: false, events: ['call_ended'], auth_type: 'none', auth_value: '' },
            report_config: result.data.report_config || { enabled: false, email: '', include_summary: true, include_transcript: true, include_caller_info: true, include_tools_used: true },
            twilio_config: result.data.twilio_config || { account_sid: '', auth_token: '', phone_number: '' },
            call_settings: result.data.call_settings || { silence_timeout: 60, max_duration: 600 },
            voice_settings: result.data.voice_settings || {},
            voice_config: result.data.voice_config || { stability: 0.5, similarity_boost: 0.75, speed: 1.0, temperature: 0.7, turn_timeout: 3.0, filler_message: '', human_transfer_number: '', tts_model: 'eleven_turbo_v2_5' },
            inbound_greeting: result.data.inbound_greeting || '', outbound_greeting: result.data.outbound_greeting || '',
            greeting_mode: result.data.greeting_mode || 'auto', voice_engine: result.data.voice_engine || 'elevenlabs',
            elevenlabs_agent_id: result.data.elevenlabs_agent_id || '', elevenlabs_voice_id: result.data.elevenlabs_voice_id || '',
          });
          setKnowledgeSources(result.data.knowledge_sources || []);
          // Snapshot for unsaved changes detection
          setSavedFormSnapshot(JSON.stringify({
            name: result.data.name || '', description: result.data.description || '',
            system_prompt: result.data.system_prompt || '', language: result.data.language || 'de',
            phone_number: result.data.phone_number || '', is_active: result.data.is_active ?? true,
            tools: result.data.tools || [], inbound_greeting: result.data.inbound_greeting || '',
            elevenlabs_voice_id: result.data.elevenlabs_voice_id || '',
          }));
        } else {
          setLoadError('Agent konnte nicht geladen werden. Bitte versuche es erneut.');
        }
      } catch (err) {
        setLoadError('Verbindungsfehler beim Laden des Agenten.');
      }
      setLoading(false);
    };
    loadAgent();
  }, [agentId]);

  // Sync knowledge sources with formData
  useEffect(() => { if (formData?.knowledge_sources) setKnowledgeSources(formData.knowledge_sources); }, [formData?.knowledge_sources]);

  // Detect unsaved changes
  useEffect(() => {
    if (!formData || !savedFormSnapshot) return;
    const currentSnapshot = JSON.stringify({
      name: formData.name, description: formData.description,
      system_prompt: formData.system_prompt, language: formData.language,
      phone_number: formData.phone_number, is_active: formData.is_active,
      tools: formData.tools, inbound_greeting: formData.inbound_greeting,
      elevenlabs_voice_id: formData.elevenlabs_voice_id,
    });
    setHasUnsavedChanges(currentSnapshot !== savedFormSnapshot);
  }, [formData, savedFormSnapshot]);

  // Notify parent about dirty state
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  // Warn on browser close/refresh with unsaved changes
  useEffect(() => {
    const handler = (e) => { if (hasUnsavedChanges) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // ========== HANDLERS ==========

  // Safe navigation back - warn about unsaved changes
  const handleBack = async () => {
    if (hasUnsavedChanges) {
      const ok = await confirm({
        title: 'Ungespeicherte Änderungen',
        message: 'Sie haben Änderungen vorgenommen, die noch nicht gespeichert wurden. Möchten Sie die Seite wirklich verlassen?',
        confirmText: 'Verlassen',
        cancelText: 'Zurück zum Editor',
        type: 'warning',
      });
      if (ok) onBack();
    } else {
      onBack();
    }
  };

  const loadElVoices = useCallback(async () => {
    if (elVoices.length > 0) return;
    setElVoicesLoading(true);
    try { const res = await api.get('/elevenlabs/voices'); if (res.success) setElVoices(res.voices || []); } catch (e) { /* ignored */ }
    setElVoicesLoading(false);
  }, [elVoices.length]);

  const playPreviewEditor = (voiceId, previewUrl) => {
    if (audioRefEditor.current) { audioRefEditor.current.pause(); audioRefEditor.current = null; }
    if (playingVoice === voiceId) { setPlayingVoice(null); return; }
    const audio = new Audio(previewUrl);
    audio.onended = () => setPlayingVoice(null);
    audio.play().catch(() => setPlayingVoice(null));
    audioRefEditor.current = audio;
    setPlayingVoice(voiceId);
  };

  const connectTwilioToElevenLabs = async () => {
    const sid = formData.twilio_config?.account_sid;
    const token = formData.twilio_config?.auth_token;
    const phone = formData.twilio_config?.phone_number || formData.phone_number;
    if (!sid || !token || !phone) { setElMessage({ type: 'error', text: 'Bitte zuerst Twilio-Credentials im Tab "Verbindungen" eintragen.' }); return; }
    setElConnecting(true); setElMessage(null);
    try {
      const res = await api.post(`/elevenlabs/agents/${agentId}/connect-twilio`, { phone_number: phone, twilio_account_sid: sid, twilio_auth_token: token });
      if (res.success) { setElMessage({ type: 'success', text: 'Twilio-Nummer erfolgreich verbunden! Eingehende Anrufe werden jetzt von Wisecall bearbeitet.' }); }
      else { setElMessage({ type: 'error', text: res.detail || 'Verbindung fehlgeschlagen' }); }
    } catch (e) { setElMessage({ type: 'error', text: e.message }); }
    setElConnecting(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = { ...formData, voice_engine: 'elevenlabs', knowledge_sources: knowledgeSources.length > 0 ? knowledgeSources : formData.knowledge_sources, callback_base_url: window.location.origin };
      const result = await onSave(dataToSave);
      if (result.success) {
        setAgent({ ...agent, ...dataToSave });
        // Reset unsaved changes tracking
        setSavedFormSnapshot(JSON.stringify({
          name: formData.name, description: formData.description,
          system_prompt: formData.system_prompt, language: formData.language,
          phone_number: formData.phone_number, is_active: formData.is_active,
          tools: formData.tools, inbound_greeting: formData.inbound_greeting,
          elevenlabs_voice_id: formData.elevenlabs_voice_id,
        }));
        setHasUnsavedChanges(false);
        if (result.synced) {
          const details = result.sync_details || {};
          let detailText = '';
          if (details.voice_synced) detailText += ' Stimme,';
          if (details.prompt_synced) detailText += ` Prompt (${details.prompt_length} Zeichen),`;
          if (details.tools_synced > 0) detailText += ` ${details.tools_synced} Tools,`;
          if (details.phone_connected) detailText += ' Telefon verbunden,';
          detailText = detailText.replace(/,$/, '');
          const prefix = result.auto_created ? 'KI-Agent erstellt und synchronisiert' : 'Gespeichert und synchronisiert';
          setElMessage({ type: 'success', text: `${prefix}${detailText ? ':' + detailText : ''}` });
        } else if (result.sync_error) {
          const phoneErr = result.sync_details?.phone_error;
          setElMessage({ type: 'warning', text: phoneErr ? `Gespeichert und synchronisiert, aber Telefon-Verbindung fehlgeschlagen: ${phoneErr}` : `Gespeichert, aber Sync fehlgeschlagen: ${result.sync_error}` });
        } else if (!result.elevenlabs_agent_id) {
          setElMessage({ type: 'warning', text: 'Gespeichert. KI-Agent konnte nicht erstellt werden — bitte erneut versuchen.' });
        } else {
          setElMessage({ type: 'success', text: 'Gespeichert' });
        }
        setTimeout(() => setElMessage(null), 8000);
      } else {
        setElMessage({ type: 'error', text: result.error || 'Fehler beim Speichern' });
        setTimeout(() => setElMessage(null), 8000);
      }
    } catch (err) {
      setElMessage({ type: 'error', text: 'Verbindungsfehler beim Speichern' });
      setTimeout(() => setElMessage(null), 8000);
    }
    setSaving(false);
  };

  const checkSyncStatus = async () => {
    setCheckingSync(true); setSyncStatus(null);
    try { const result = await api.get(`/agents/${agentId}/sync-status`); setSyncStatus(result); } catch (err) { setSyncStatus({ success: false, status: 'error', message: 'Verbindungsfehler' }); }
    setCheckingSync(false);
  };

  const testTwilioConfig = async () => {
    setTwilioTestLoading(true); setTwilioTestResult(null);
    try {
      const response = await fetch(`${API_URL}/api/agents/${agentId}/twilio/test`, { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` } });
      setTwilioTestResult(await response.json());
    } catch (error) { setTwilioTestResult({ success: false, status: 'error', message: 'Verbindungsfehler: ' + error.message }); }
    setTwilioTestLoading(false);
  };

  const hangUpCall = async () => {
    if (!activeCallSid) return;
    try { await fetch(`${API_URL}/api/calls/${activeCallSid}/hangup`, { method: 'POST', headers: getAuthHeaders() }); } catch (error) { /* ignored */ }
    setCallStatus('completed'); setCallStartTime(null); setCallElapsed(0);
    setOutboundResult(prev => ({ ...prev, finalStatus: 'completed', message: 'Anruf wurde beendet' }));
    setActiveCallSid(null);
  };

  const initiateOutboundCall = async () => {
    if (!outboundNumber.trim()) return;
    setOutboundLoading(true); setOutboundResult(null); setCallStatus('initiating');
    try {
      const result = await api.post('/elevenlabs/outbound-call', { agent_id: agentId, to_number: outboundNumber.trim(), first_message: outboundContext || null });
      if (result.success) {
        const trackingId = result.conversation_id || result.call_sid;
        setOutboundResult({ success: true, message: 'Anruf wird aufgebaut...', to_number: outboundNumber, conversation_id: result.conversation_id, call_sid: result.call_sid });
        setCallStatus('ringing'); setActiveCallSid(trackingId); setCallStartTime(Date.now()); setCallElapsed(0);
      } else {
        setOutboundResult({ success: false, message: result.detail || result.message || 'Anruf konnte nicht gestartet werden' }); setCallStatus(null);
      }
    } catch (error) { setOutboundResult({ success: false, message: error.message || String(error) }); setCallStatus(null); }
    setOutboundLoading(false);
  };

  const optimizePrompt = async () => {
    const textToOptimize = formData.system_prompt?.trim();
    if (!textToOptimize || optimizing) return;
    setOptimizing(true);
    try {
      const response = await fetch(`${API_URL}/api/optimize-prompt`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }, body: JSON.stringify({ input: textToOptimize, agent_name: formData.name || 'Agent', language: formData.language || 'de' }) });
      const result = await response.json();
      if (result.success) { setFormData({ ...formData, system_prompt: result.data.optimized_prompt }); }
      else { toast.error(result.detail || 'Optimierung fehlgeschlagen'); }
    } catch (error) { toast.error(error.message); }
    finally { setOptimizing(false); }
  };

  // Knowledge handlers
  const addKnowledgeUrl = async () => {
    if (!newUrl.trim() || urlLoading) return;
    setUrlLoading(true); setFoundSubpages([]); setSelectedSubpages([]);
    try {
      const response = await fetch(`${API_URL}/api/agents/${agentId}/knowledge/url`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ url: newUrl, name: newUrl, use_javascript: scrapeWithJs, include_subpages: includeSubpages }) });
      const result = await response.json();
      if (result.success) { setKnowledgeSources(prev => [...prev, result.data]); setNewUrl(''); if (result.data.subpages_found?.length > 0) setFoundSubpages(result.data.subpages_found); }
      else { toast.error(result.detail || 'URL konnte nicht hinzugefügt werden'); }
    } catch (error) { toast.error(error.message); }
    finally { setUrlLoading(false); }
  };

  const scrapeSelectedSubpages = async () => {
    if (selectedSubpages.length === 0 || subpageLoading) return;
    setSubpageLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/agents/${agentId}/knowledge/scrape-subpages`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ urls: selectedSubpages }) });
      const result = await response.json();
      if (result.success) {
        const agentResponse = await fetch(`${API_URL}/api/agents/${agentId}/knowledge`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` } });
        const agentResult = await agentResponse.json();
        if (agentResult.success) setKnowledgeSources(agentResult.data);
        setFoundSubpages([]); setSelectedSubpages([]);
        toast.success(`${result.data.filter(r => r.success).length} von ${result.data.length} Seiten erfolgreich importiert`);
      }
    } catch (error) { toast.error(error.message); }
    finally { setSubpageLoading(false); }
  };

  const uploadDocument = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.warning('Datei zu groß (max. 10 MB)');
      event.target.value = '';
      return;
    }
    setUploadLoading(true);
    try {
      // Read file as base64 to avoid multipart CORS/proxy issues
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const response = await fetch(`${API_URL}/api/agents/${agentId}/knowledge/document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ filename: file.name, data: base64Data }),
      });
      const result = await response.json();
      if (result.success) { setKnowledgeSources(prev => [...prev, result.data]); } else { toast.error(result.detail || 'Dokument konnte nicht hochgeladen werden'); }
    } catch (error) { toast.error(error.message); }
    finally { setUploadLoading(false); event.target.value = ''; }
  };

  const deleteKnowledgeSource = async (sourceId) => {
    const ok = await confirm({
      title: 'Wissensquelle löschen',
      message: 'Diese Wissensquelle wird unwiderruflich entfernt. Fortfahren?',
      confirmText: 'Löschen',
      type: 'danger',
    });
    if (!ok) return;
    try {
      const response = await fetch(`${API_URL}/api/agents/${agentId}/knowledge/${sourceId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` } });
      const result = await response.json();
      if (result.success) { setKnowledgeSources(prev => prev.filter(s => s.id !== sourceId)); if (formData) setFormData(prev => ({ ...prev, knowledge_sources: (prev.knowledge_sources || []).filter(s => s.id !== sourceId) })); toast.success('Wissensquelle gelöscht'); }
      else { toast.error(result.detail || 'Fehler beim Löschen'); }
    } catch (error) { toast.error(error.message); }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]); setChatInput(''); setChatLoading(true);
    try {
      const cleanHistory = chatMessages.map(msg => ({ role: msg.role, content: msg.content }));
      const response = await fetch(`${API_URL}/api/agents/${agentId}/chat`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: chatInput, conversation_history: cleanHistory }) });
      const result = await response.json();
      if (result.success) { setChatMessages(prev => [...prev, { role: 'assistant', content: result.data.message, tool: result.data.tool_triggered }]); }
      else { const errorContent = typeof result.detail === 'string' ? result.detail : (result.detail?.msg || JSON.stringify(result.detail) || 'Fehler beim Senden'); setChatMessages(prev => [...prev, { role: 'error', content: errorContent }]); }
    } catch (error) { setChatMessages(prev => [...prev, { role: 'error', content: error.message || 'Unbekannter Fehler' }]); }
    finally { setChatLoading(false); }
  };

  // ========== RENDER ==========

  const tabs = [
    { id: 'basics', label: '📝 Grundlagen', shortLabel: '📝' },
    { id: 'voice', label: '🔊 Stimme', shortLabel: '🔊' },
    { id: 'knowledge', label: '📚 Wissensbasis', shortLabel: '📚' },
    { id: 'tools', label: '🔧 Tools', shortLabel: '🔧' },
    { id: 'automation', label: '⚡ Automatisierung', shortLabel: '⚡' },
    { id: 'outbound', label: '📞 Anrufen', shortLabel: '📞' },
    { id: 'connections', label: '🔌 Verbindungen', shortLabel: '🔌' }
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div></div>;

  if (loadError || !formData) return (
    <div className="flex flex-col items-center justify-center h-64 gap-4" data-testid="agent-load-error">
      <div className="text-red-500 text-lg font-medium">{loadError || 'Agent konnte nicht geladen werden'}</div>
      <div className="flex gap-3">
        <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Seite neu laden</button>
        <button onClick={handleBack} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">Zurück zu Agenten</button>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 bg-white border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-4">
          <h1 className="text-lg sm:text-xl font-bold text-gray-800 truncate">{formData.name || 'Neuer Agent'}</h1>
          <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${formData.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{formData.is_active ? '● Aktiv' : '○ Inaktiv'}</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button onClick={() => setFormData({ ...formData, is_active: !formData.is_active })} className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${formData.is_active ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>{formData.is_active ? '⏸ Deaktiv.' : '▶ Aktivieren'}</button>
          <button onClick={checkSyncStatus} disabled={checkingSync} className="flex-shrink-0 px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-xs" data-testid="check-sync-btn">{checkingSync ? '...' : 'Sync prüfen'}</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 text-xs sm:text-sm">{saving ? 'Speichere...' : 'Speichern'}</button>
        </div>
      </div>

      {/* Status Banners */}
      {elMessage && (
        <div className={`flex-shrink-0 px-4 sm:px-6 py-2 text-sm font-medium ${elMessage.type === 'success' ? 'bg-green-50 text-green-800 border-b border-green-200' : elMessage.type === 'warning' ? 'bg-yellow-50 text-yellow-800 border-b border-yellow-200' : 'bg-red-50 text-red-800 border-b border-red-200'}`} data-testid="save-status-banner">
          {elMessage.type === 'success' && '✓ '}{elMessage.type === 'warning' && '⚠ '}{elMessage.type === 'error' && '✕ '}{elMessage.text}
        </div>
      )}

      {syncStatus && (
        <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50 text-xs" data-testid="sync-status-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-gray-700">Sync-Status: {syncStatus.status === 'synced' ? '✓ Synchron' : syncStatus.status === 'not_linked' ? '⚠ Nicht verknüpft' : syncStatus.status === 'out_of_sync' ? '⚠ Nicht synchron' : '✕ Fehler'}</span>
            <button onClick={() => setSyncStatus(null)} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          {syncStatus.issues?.length > 0 && <div className="space-y-1 mb-2">{syncStatus.issues.map((issue, i) => <div key={i} className="text-amber-700 bg-amber-50 px-2 py-1 rounded">{issue}</div>)}</div>}
          {syncStatus.elevenlabs && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-600">
              <div>EL Voice: <span className="font-mono">{syncStatus.elevenlabs.voice_id || '—'}</span></div>
              <div>EL Prompt: {syncStatus.elevenlabs.prompt_length} Zeichen</div>
              <div>EL end_call: {syncStatus.elevenlabs.has_end_call ? '✓' : '✕'}</div>
              <div>EL Wissensbasis: {syncStatus.elevenlabs.has_knowledge_base ? '✓' : '✕'}</div>
              <div>EL Sprache: {syncStatus.elevenlabs.language || '—'}</div>
              <div>EL Temp: {syncStatus.elevenlabs.temperature ?? '—'}</div>
            </div>
          )}
          {syncStatus.status === 'not_linked' && <div className="text-amber-700 mt-1">{syncStatus.message}</div>}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* LEFT PANEL */}
        <div className={`flex-1 ${!previewCollapsed ? 'lg:w-1/2' : ''} lg:border-r border-gray-200 flex flex-col overflow-hidden transition-all duration-300`}>
          {/* Tab Navigation */}
          <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50">
            {/* Mobile dropdown */}
            <div className="sm:hidden px-4 py-3">
              <div className="relative">
                <button onClick={() => setMobileTabsOpen(!mobileTabsOpen)} className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl text-left">
                  <span className="font-medium text-gray-800">{tabs.find(t => t.id === activeTab)?.label}</span>
                  <svg className={`w-5 h-5 text-gray-400 transition-transform ${mobileTabsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {mobileTabsOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setMobileTabsOpen(false)} />
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                      {tabs.map((tab) => (
                        <button key={tab.id} onClick={() => { setActiveTab(tab.id); setMobileTabsOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${activeTab === tab.id ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>
                          <span className="text-lg">{tab.shortLabel}</span>
                          <span>{tab.label.replace(tab.shortLabel + ' ', '')}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Desktop tabs */}
            <div className="hidden sm:flex px-4 gap-1 items-center">
              {tabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-3 lg:px-4 py-2 lg:py-3 text-xs lg:text-sm font-medium transition-colors rounded-t-lg whitespace-nowrap ${activeTab === tab.id ? 'text-indigo-600 bg-white border-t border-l border-r border-gray-200 -mb-px' : 'text-gray-500 hover:text-gray-700'}`}>{tab.label}</button>
              ))}
              {previewCollapsed && (
                <button onClick={() => setPreviewCollapsed(false)} className="ml-auto px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg flex items-center gap-2 transition-colors" data-testid="expand-preview-btn">
                  <span>💬</span> <span className="hidden lg:inline">Preview öffnen</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-white">
            {activeTab === 'basics' && <BasicsTab formData={formData} setFormData={setFormData} optimizing={optimizing} optimizePrompt={optimizePrompt} />}
            {activeTab === 'voice' && <VoiceTab formData={formData} setFormData={setFormData} agent={agent} elVoices={elVoices} elVoicesLoading={elVoicesLoading} loadElVoices={loadElVoices} playingVoice={playingVoice} playPreviewEditor={playPreviewEditor} elMessage={elMessage} elConnecting={elConnecting} connectTwilioToElevenLabs={connectTwilioToElevenLabs} />}
            {activeTab === 'knowledge' && <KnowledgeTab formData={formData} knowledgeSources={knowledgeSources} newUrl={newUrl} setNewUrl={setNewUrl} urlLoading={urlLoading} scrapeWithJs={scrapeWithJs} setScrapeWithJs={setScrapeWithJs} includeSubpages={includeSubpages} setIncludeSubpages={setIncludeSubpages} addKnowledgeUrl={addKnowledgeUrl} foundSubpages={foundSubpages} selectedSubpages={selectedSubpages} setSelectedSubpages={setSelectedSubpages} subpageLoading={subpageLoading} scrapeSelectedSubpages={scrapeSelectedSubpages} setFoundSubpages={setFoundSubpages} uploadLoading={uploadLoading} uploadDocument={uploadDocument} deleteKnowledgeSource={deleteKnowledgeSource} />}
            {activeTab === 'tools' && <ToolsTab formData={formData} setFormData={setFormData} onEditTool={(tool) => { setEditingTool(tool); setShowToolModal(true); }} />}
            {activeTab === 'automation' && <AutomationTab formData={formData} setFormData={setFormData} />}
            {activeTab === 'outbound' && <OutboundTab formData={formData} agent={agent} activeCallSid={activeCallSid} callStatus={callStatus} callElapsed={callElapsed} outboundNumber={outboundNumber} setOutboundNumber={setOutboundNumber} outboundContext={outboundContext} setOutboundContext={setOutboundContext} outboundLoading={outboundLoading} outboundResult={outboundResult} initiateOutboundCall={initiateOutboundCall} hangUpCall={hangUpCall} setActiveTab={setActiveTab} />}
            {activeTab === 'connections' && <ConnectionsTab formData={formData} setFormData={setFormData} twilioTestLoading={twilioTestLoading} twilioTestResult={twilioTestResult} testTwilioConfig={testTwilioConfig} />}
          </div>
        </div>

        {/* RIGHT PANEL - Live Preview */}
        <LivePreview previewCollapsed={previewCollapsed} setPreviewCollapsed={setPreviewCollapsed} previewMode={previewMode} setPreviewMode={setPreviewMode} formData={formData} chatMessages={chatMessages} setChatMessages={setChatMessages} chatInput={chatInput} setChatInput={setChatInput} chatLoading={chatLoading} sendChatMessage={sendChatMessage} activeCallSid={activeCallSid} callStatus={callStatus} outboundNumber={outboundNumber} setOutboundNumber={setOutboundNumber} outboundContext={outboundContext} setOutboundContext={setOutboundContext} outboundLoading={outboundLoading} outboundResult={outboundResult} setOutboundResult={setOutboundResult} setCallStatus={setCallStatus} initiateOutboundCall={initiateOutboundCall} hangUpCall={hangUpCall} />
      </div>

      {/* Mobile Floating Test Button */}
      <div className="lg:hidden fixed bottom-4 right-4 z-40">
        <button onClick={() => setPreviewCollapsed(false)} className="w-14 h-14 bg-indigo-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-indigo-700 transition-colors" aria-label="Agent testen"><span className="text-2xl">💬</span></button>
      </div>

      {/* Tool Edit Modal */}
      {showToolModal && editingTool && (
        <ToolConfigModal
          tool={editingTool}
          agentId={agent?.id}
          onClose={() => { setShowToolModal(false); setEditingTool(null); }}
          onSave={(updatedTool) => {
            const newTools = [...(formData.tools || [])];
            if (editingTool.index === -1) { newTools.push(updatedTool); } else { newTools[editingTool.index] = updatedTool; }
            setFormData({ ...formData, tools: newTools });
            setShowToolModal(false); setEditingTool(null);
          }}
          onDelete={() => {
            if (editingTool.index >= 0) { setFormData({ ...formData, tools: formData.tools.filter((_, i) => i !== editingTool.index) }); }
            setShowToolModal(false); setEditingTool(null);
          }}
        />
      )}
    </div>
  );
}
